import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import TabNavigation from './ui/TabNavigation';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '../utils/notify';
import {
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  ChatBubbleLeftEllipsisIcon,
  Squares2X2Icon,
  ListBulletIcon,
  FireIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { DataTable } from './common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RiskRegister = () => {
  const { user, token } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [dashboardStats, setDashboardStats] = useState(null);
  const [risks, setRisks] = useState([]);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [assets, setAssets] = useState([]);
  const [controls, setControls] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  
  // Filter states
  const [filters, setFilters] = useState({
    category: '',
    severity: '',
    status: '',
    assigned_to: '',
    business_unit: '',
    overdue: null,
    search: ''
  });
  
  // Modal states
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showRiskDetailModal, setShowRiskDetailModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  
  // Form data
  const [riskFormData, setRiskFormData] = useState({
    risk_title: '',
    risk_description: '',
    business_unit_asset: '',
    risk_category: 'Operational',
    likelihood: 3,
    impact: 3,
    existing_controls: [],
    control_effectiveness: 'None',
    treatment_option: 'Mitigate',
    remediation_actions: '',
    assigned_to: '',
    due_date: '',
    status: 'Open',
    progress: 0
  });
  
  const [noteFormData, setNoteFormData] = useState({ content: '' });

  // Constants
  const riskCategories = ['Operational', 'Compliance', 'Cybersecurity', 'Data Privacy', 'Third-Party', 'Cloud', 'Other'];
  const riskSeverities = ['Critical', 'High', 'Medium', 'Low'];
  const riskStatuses = ['Open', 'In-progress', 'Closed'];
  const controlEffectiveness = ['High', 'Medium', 'Low', 'None'];
  const treatmentOptions = ['Mitigate', 'Accept', 'Transfer', 'Avoid'];

  useEffect(() => {
    loadDashboardData();
    loadRisks();
    loadAssets();
    loadControls();
  }, []);

  useEffect(() => {
    loadRisks();
  }, [filters, pagination.offset]);

  const apiCall = async (endpoint, method = 'GET', data = null) => {
    try {
      const config = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (data && method !== 'GET') {
        config.body = JSON.stringify(data);
      }
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API call failed for ${endpoint}:`, error);
      toast.error(`API Error: ${error.message}`);
      throw error;
    }
  };

  const loadDashboardData = async () => {
    try {
      const stats = await apiCall('/api/risk-register/dashboard/stats');
      setDashboardStats(stats);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    }
  };

  const loadRisks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString()
      });
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== '') params.append(key, value);
      });
      
      const response = await apiCall(`/api/risk-register/risks?${params}`);
      setRisks(response.risks || []);
      setPagination(prev => ({ 
        ...prev, 
        total: response.total || 0,
        hasMore: response.has_more || false
      }));
    } catch (error) {
      console.error('Failed to load risks:', error);
      setRisks([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      const response = await apiCall('/api/assets?limit=100');
      setAssets(response.assets || []);
    } catch (error) {
      console.error('Failed to load assets:', error);
    }
  };

  const loadControls = async () => {
    try {
      const frameworks = ['iso-27001-2022', 'soc2-type2', 'gdpr', 'hipaa'];
      let allControls = [];
      
      for (const frameworkId of frameworks) {
        try {
          const response = await apiCall(`/api/trust-center/frameworks/${frameworkId}/controls`);
          allControls = [...allControls, ...(response || [])];
        } catch (error) {
          console.error(`Failed to load controls for ${frameworkId}:`, error);
        }
      }
      
      setControls(allControls);
    } catch (error) {
      console.error('Failed to load controls:', error);
    }
  };

  const handleCreateRisk = async () => {
    try {
      const submitData = { ...riskFormData };
      if (submitData.due_date) {
        submitData.due_date = new Date(submitData.due_date).toISOString();
      }
      
      await apiCall('/api/risk-register/risks', 'POST', submitData);
      toast.success('Risk created successfully');
      setShowRiskModal(false);
      resetRiskForm();
      loadRisks();
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to create risk');
    }
  };

  const handleUpdateRisk = async (riskId, updateData) => {
    try {
      await apiCall(`/api/risk-register/risks/${riskId}`, 'PUT', updateData);
      toast.success('Risk updated successfully');
      loadRisks();
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update risk');
    }
  };

  const handleDeleteRisk = async (riskId) => {
    if (window.confirm('Are you sure you want to delete this risk?')) {
      try {
        await apiCall(`/api/risk-register/risks/${riskId}`, 'DELETE');
        toast.success('Risk deleted successfully');
        loadRisks();
        loadDashboardData();
      } catch (error) {
        toast.error('Failed to delete risk');
      }
    }
  };

  const handleAddNote = async (riskId) => {
    try {
      await apiCall(`/api/risk-register/risks/${riskId}/notes`, 'POST', noteFormData);
      toast.success('Note added successfully');
      setShowNoteModal(false);
      setNoteFormData({ content: '' });
      if (selectedRisk) {
        await viewRiskDetails(riskId);
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const viewRiskDetails = async (riskId) => {
    try {
      const response = await apiCall(`/api/risk-register/risks/${riskId}`);
      setSelectedRisk(response);
      setShowRiskDetailModal(true);
    } catch (error) {
      toast.error('Failed to load risk details');
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      toast.loading('Generating export...', { id: 'export' });
      const response = await fetch(`${BACKEND_URL}/api/risk-register/export?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `risk_register_export_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded', { id: 'export' });
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast.error('Export failed', { id: 'export' });
    }
  };

  const resetRiskForm = () => {
    setRiskFormData({
      risk_title: '',
      risk_description: '',
      business_unit_asset: '',
      risk_category: 'Operational',
      likelihood: 3,
      impact: 3,
      existing_controls: [],
      control_effectiveness: 'None',
      treatment_option: 'Mitigate',
      remediation_actions: '',
      assigned_to: '',
      due_date: '',
      status: 'Open',
      progress: 0
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'text-red-800 bg-red-100 border-red-200';
      case 'High': return 'text-orange-800 bg-orange-100 border-orange-200';
      case 'Medium': return 'text-yellow-800 bg-yellow-100 border-yellow-200';
      case 'Low': return 'text-green-800 bg-green-100 border-green-200';
      default: return 'text-gray-800 bg-gray-100 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'text-red-600 bg-red-50 border-red-200';
      case 'In-progress': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Closed': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Operational': return <BuildingOfficeIcon className="w-5 h-5" />;
      case 'Compliance': return <ShieldCheckIcon className="w-5 h-5" />;
      case 'Cybersecurity': return <FireIcon className="w-5 h-5" />;
      case 'Data Privacy': return <DocumentTextIcon className="w-5 h-5" />;
      case 'Third-Party': return <UserIcon className="w-5 h-5" />;
      case 'Cloud': return <CloudArrowUpIcon className="w-5 h-5" />;
      default: return <ExclamationTriangleIcon className="w-5 h-5" />;
    }
  };

  // Dashboard Component
  const DashboardView = () => (
    <div className="space-y-6">
      {/* KPIs */}
      {dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Risks</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_risks}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <FireIcon className="w-8 h-8 text-red-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Critical Risks</p>
                <p className="text-2xl font-bold text-red-600">{dashboardStats.risks_by_severity?.Critical || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <ClockIcon className="w-8 h-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Overdue Risks</p>
                <p className="text-2xl font-bold text-yellow-600">{dashboardStats.overdue_risks}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Closed Risks</p>
                <p className="text-2xl font-bold text-green-600">{dashboardStats.risks_by_status?.Closed || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Heatmap */}
      {dashboardStats && dashboardStats.heatmap_data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Heatmap (Likelihood vs Impact)</h3>
          <div className="grid grid-cols-5 gap-2">
            {Array.from({length: 25}, (_, index) => {
              const likelihood = Math.floor(index / 5) + 1;
              const impact = (index % 5) + 1;
              const cellData = dashboardStats.heatmap_data.find(d => d.likelihood === likelihood && d.impact === impact);
              const count = cellData?.count || 0;
              const severity = cellData?.severity || 'Low';
              
              return (
                <div
                  key={index}
                  className={`h-16 w-16 border rounded flex items-center justify-center text-sm font-bold ${
                    severity === 'Critical' ? 'bg-red-500 text-white' :
                    severity === 'High' ? 'bg-orange-400 text-white' :
                    severity === 'Medium' ? 'bg-yellow-300 text-black' :
                    'bg-green-200 text-black'
                  } ${count === 0 ? 'opacity-50' : ''}`}
                  title={`L${likelihood} × I${impact} = ${count} risks`}
                >
                  {count}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-4 text-sm text-gray-600">
            <span>Impact →</span>
            <span>← Likelihood</span>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risks by Severity */}
        {dashboardStats && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Risks by Severity</h3>
            <div className="space-y-3">
              {Object.entries(dashboardStats.risks_by_severity || {}).map(([severity, count]) => (
                <div key={severity} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${
                      severity === 'Critical' ? 'bg-red-500' :
                      severity === 'High' ? 'bg-orange-400' :
                      severity === 'Medium' ? 'bg-yellow-400' :
                      'bg-green-400'
                    }`}></div>
                    <span className="text-sm font-medium text-gray-700">{severity}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Risks */}
        {dashboardStats && dashboardStats.top_risks && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Risks</h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {dashboardStats.top_risks.map((risk, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{risk.risk_title}</p>
                    <p className="text-xs text-gray-600">{risk.asset_name}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(risk.risk_severity)}`}>
                      {risk.risk_severity}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">Score: {risk.residual_risk_score}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const risksColumns = [
    {
      id: "risk",
      header: "Risk",
      cell: ({ row }) => {
        const risk = row.original;
        return (
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
              {getCategoryIcon(risk.risk_category)}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">{risk.risk_title}</div>
              <div className="text-sm text-gray-500">{risk.asset_name || 'No asset assigned'}</div>
              {risk.overdue && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1">
                  Overdue
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "risk_category",
      header: "Category",
      cell: ({ getValue }) => <span className="text-sm text-gray-900">{getValue()}</span>,
    },
    {
      accessorKey: "risk_severity",
      header: "Severity",
      cell: ({ getValue }) => (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(getValue())}`}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(getValue())}`}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: "assigned_to",
      header: "Assigned To",
      cell: ({ getValue }) => <span className="text-sm text-gray-900">{getValue() || 'Unassigned'}</span>,
    },
    {
      accessorKey: "due_date",
      header: "Due Date",
      cell: ({ getValue }) => <span className="text-sm text-gray-900">{getValue() ? new Date(getValue()).toLocaleDateString() : 'No due date'}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const risk = row.original;
        return (
          <div className="space-x-2">
            <button onClick={() => viewRiskDetails(risk.id)} className="text-blue-600 hover:text-blue-900">
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setRiskFormData({
                  risk_title: risk.risk_title,
                  risk_description: risk.risk_description,
                  business_unit_asset: risk.business_unit_asset || '',
                  risk_category: risk.risk_category,
                  likelihood: risk.likelihood,
                  impact: risk.impact,
                  existing_controls: risk.existing_controls || [],
                  control_effectiveness: risk.control_effectiveness,
                  treatment_option: risk.treatment_option,
                  remediation_actions: risk.remediation_actions || '',
                  assigned_to: risk.assigned_to || '',
                  due_date: risk.due_date ? risk.due_date.split('T')[0] : '',
                  status: risk.status,
                  progress: risk.progress,
                });
                setSelectedRisk(risk);
                setShowRiskModal(true);
              }}
              className="text-yellow-600 hover:text-yellow-900"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            {user?.role === 'admin' && (
              <button onClick={() => handleDeleteRisk(risk.id)} className="text-red-600 hover:text-red-900">
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // Risks List Component
  const RisksListView = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search risks..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {riskCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          
          <select
            value={filters.severity}
            onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Severities</option>
            {riskSeverities.map(severity => (
              <option key={severity} value={severity}>{severity}</option>
            ))}
          </select>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Status</option>
            {riskStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.overdue === true}
                onChange={(e) => setFilters(prev => ({ ...prev, overdue: e.target.checked ? true : null }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Show overdue only</span>
            </label>
            
            <button
              onClick={() => setFilters({ category: '', severity: '', status: '', assigned_to: '', business_unit: '', overdue: null, search: '' })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
          
          <button
            onClick={() => setShowRiskModal(true)}
            className="px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700"
          >
            <PlusIcon className="w-4 h-4 inline mr-2" />
            Add New Risk
          </button>
        </div>
      </div>

      {/* Risks Table */}
      <DataTable
        columns={risksColumns}
        data={risks}
        getRowId={(risk) => String(risk.id)}
        loading={loading}
        emptyMessage="No risks found"
        manualPagination
        rowCount={pagination.total || 0}
        pagination={{ pageIndex: Math.floor((pagination.offset || 0) / pagination.limit), pageSize: pagination.limit }}
        onPaginationChange={(updater) => {
          const current = { pageIndex: Math.floor((pagination.offset || 0) / pagination.limit), pageSize: pagination.limit };
          const next = typeof updater === "function" ? updater(current) : updater;
          setPagination((p) => ({ ...p, limit: next.pageSize, offset: next.pageIndex * next.pageSize }));
        }}
        enableColumnVisibility={false}
        enableColumnResizing
        maxBodyHeight="65vh"
      />
    </div>
  );

  // Main render
  if (loading && !dashboardStats) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Risk Register</h1>
        <p className="text-gray-600">
          Comprehensive risk management with automated scoring, heatmap analysis, and integrated controls
        </p>
      </div>

      {/* Tab Navigation */}
      <TabNavigation
        tabs={[
          { id: 'dashboard', name: 'Risk Dashboard', icon: Squares2X2Icon },
          { id: 'risks', name: 'Risk Register', icon: ListBulletIcon },
          { id: 'reports', name: 'Reports', icon: DocumentTextIcon }
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Export Actions */}
      <div className="mb-6 flex justify-end space-x-2">
        <button
          onClick={() => handleExport('csv')}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
          Export CSV
        </button>
        <button
          onClick={() => handleExport('excel')}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
          Export Excel
        </button>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'dashboard' && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <DashboardView />
          </motion.div>
        )}

        {activeTab === 'risks' && (
          <motion.div
            key="risks"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <RisksListView />
          </motion.div>
        )}

        {activeTab === 'reports' && (
          <motion.div
            key="reports"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Risk Reports</h3>
              <p className="text-gray-600 mb-4">
                Generate comprehensive risk analysis reports and compliance matrices
              </p>
              <div className="space-x-4">
                <button 
                  onClick={() => handleExport('csv')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700"
                >
                  <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                  Risk Assessment Report
                </button>
                <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  <ChartBarIcon className="w-4 h-4 mr-2" />
                  Risk Heatmap Analysis
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Risk Modal */}
      <AnimatePresence>
        {showRiskModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedRisk ? 'Edit Risk' : 'Add New Risk'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowRiskModal(false);
                      setSelectedRisk(null);
                      resetRiskForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Section 1: Risk Details */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Section 1: Risk Details
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Risk Title *</label>
                      <input
                        type="text"
                        value={riskFormData.risk_title}
                        onChange={(e) => setRiskFormData(prev => ({ ...prev, risk_title: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter risk title"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Risk Description *</label>
                      <textarea
                        rows={3}
                        value={riskFormData.risk_description}
                        onChange={(e) => setRiskFormData(prev => ({ ...prev, risk_description: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Describe the risk in detail"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Unit / Asset</label>
                        <select
                          value={riskFormData.business_unit_asset}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, business_unit_asset: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select asset</option>
                          {assets.map(asset => (
                            <option key={asset.id} value={asset.id}>{asset.name} ({asset.business_unit})</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Risk Category *</label>
                        <select
                          value={riskFormData.risk_category}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, risk_category: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          {riskCategories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Risk Assessment */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Section 2: Risk Assessment
                  </h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Likelihood (1-5) *</label>
                        <select
                          value={riskFormData.likelihood}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, likelihood: parseInt(e.target.value) }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          {[1, 2, 3, 4, 5].map(num => (
                            <option key={num} value={num}>{num} - {['Very Low', 'Low', 'Medium', 'High', 'Very High'][num-1]}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Impact (1-5) *</label>
                        <select
                          value={riskFormData.impact}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, impact: parseInt(e.target.value) }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          {[1, 2, 3, 4, 5].map(num => (
                            <option key={num} value={num}>{num} - {['Very Low', 'Low', 'Medium', 'High', 'Very High'][num-1]}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Inherent Risk Score</label>
                        <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-700">
                          {riskFormData.likelihood * riskFormData.impact}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Existing Controls</label>
                      <select
                        multiple
                        value={riskFormData.existing_controls}
                        onChange={(e) => {
                          const values = Array.from(e.target.selectedOptions, option => option.value);
                          setRiskFormData(prev => ({ ...prev, existing_controls: values }));
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        size={4}
                      >
                        {controls.map(control => (
                          <option key={control.id} value={control.id}>
                            {control.control_id}: {control.title}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple controls</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Control Effectiveness</label>
                        <select
                          value={riskFormData.control_effectiveness}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, control_effectiveness: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {controlEffectiveness.map(eff => (
                            <option key={eff} value={eff}>{eff}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Residual Risk Score</label>
                        <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-700">
                          {Math.max(0, (riskFormData.likelihood * riskFormData.impact) - 
                            (riskFormData.control_effectiveness === 'High' ? 2 : 
                             riskFormData.control_effectiveness === 'Medium' ? 1 : 0))}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Risk Severity</label>
                        <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-700">
                          {(() => {
                            const residual = Math.max(0, (riskFormData.likelihood * riskFormData.impact) - 
                              (riskFormData.control_effectiveness === 'High' ? 2 : 
                               riskFormData.control_effectiveness === 'Medium' ? 1 : 0));
                            return residual >= 20 ? 'Critical' : residual >= 15 ? 'High' : residual >= 8 ? 'Medium' : 'Low';
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 3: Treatment Plan */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Section 3: Treatment Plan
                  </h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Treatment Option *</label>
                        <select
                          value={riskFormData.treatment_option}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, treatment_option: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          {treatmentOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
                        <input
                          type="email"
                          value={riskFormData.assigned_to}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="assignee@company.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Remediation Actions</label>
                      <textarea
                        rows={3}
                        value={riskFormData.remediation_actions}
                        onChange={(e) => setRiskFormData(prev => ({ ...prev, remediation_actions: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Describe the actions to remediate this risk"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                        <input
                          type="date"
                          value={riskFormData.due_date}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, due_date: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                          value={riskFormData.status}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, status: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {riskStatuses.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Progress (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={riskFormData.progress}
                          onChange={(e) => setRiskFormData(prev => ({ ...prev, progress: parseInt(e.target.value) || 0 }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowRiskModal(false);
                      setSelectedRisk(null);
                      resetRiskForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={selectedRisk ? 
                      () => handleUpdateRisk(selectedRisk.id, riskFormData) : 
                      handleCreateRisk
                    }
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {selectedRisk ? 'Update Risk' : 'Create Risk'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Risk Detail Modal */}
      <AnimatePresence>
        {showRiskDetailModal && selectedRisk && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Risk Details: {selectedRisk.risk_title}
                  </h3>
                  <button
                    onClick={() => {
                      setShowRiskDetailModal(false);
                      setSelectedRisk(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Main Content */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Risk Overview */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-md font-medium text-gray-900 mb-3">Risk Overview</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Category</label>
                          <p className="text-gray-900">{selectedRisk.risk_category}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(selectedRisk.risk_severity)}`}>
                            {selectedRisk.risk_severity}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Status</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(selectedRisk.status)}`}>
                            {selectedRisk.status}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</label>
                          <p className="text-gray-900">{selectedRisk.progress}%</p>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-3">Description</h4>
                      <p className="text-sm text-gray-700 bg-white border rounded-lg p-4">
                        {selectedRisk.risk_description}
                      </p>
                    </div>

                    {/* Remediation Actions */}
                    {selectedRisk.remediation_actions && (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">Remediation Actions</h4>
                        <p className="text-sm text-gray-700 bg-white border rounded-lg p-4">
                          {selectedRisk.remediation_actions}
                        </p>
                      </div>
                    )}

                    {/* Controls */}
                    {selectedRisk.control_details && selectedRisk.control_details.length > 0 && (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">Associated Controls</h4>
                        <div className="space-y-2">
                          {selectedRisk.control_details.map((control, index) => (
                            <div key={index} className="bg-white border rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-900">{control.title}</p>
                              <p className="text-xs text-gray-600">Framework: {control.framework_id}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-medium text-gray-900">Notes</h4>
                        <button
                          onClick={() => setShowNoteModal(true)}
                          className="px-3 py-1 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                        >
                          <ChatBubbleLeftEllipsisIcon className="w-4 h-4 inline mr-1" />
                          Add Note
                        </button>
                      </div>
                      
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedRisk.notes && selectedRisk.notes.length > 0 ? (
                          selectedRisk.notes.map((note, index) => (
                            <div key={index} className="bg-white border rounded-lg p-3">
                              <p className="text-sm text-gray-700">{note.content}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                By {note.created_by} on {new Date(note.created_at).toLocaleString()}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">No notes added yet</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-6">
                    {/* Risk Scores */}
                    <div className="bg-white border rounded-lg p-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Risk Assessment</h5>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Likelihood:</span>
                          <span className="font-medium">{selectedRisk.likelihood}/5</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Impact:</span>
                          <span className="font-medium">{selectedRisk.impact}/5</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-gray-600">Inherent Score:</span>
                          <span className="font-bold text-red-600">{selectedRisk.inherent_risk_score}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Control Effectiveness:</span>
                          <span className="font-medium">{selectedRisk.control_effectiveness}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-gray-600">Residual Score:</span>
                          <span className="font-bold text-blue-600">{selectedRisk.residual_risk_score}</span>
                        </div>
                      </div>
                    </div>

                    {/* Assignment Info */}
                    <div className="bg-white border rounded-lg p-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Assignment</h5>
                      <div className="space-y-3 text-sm">
                        <div>
                          <span className="text-gray-600">Assigned to:</span>
                          <p className="font-medium">{selectedRisk.assigned_to || 'Unassigned'}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Due date:</span>
                          <p className="font-medium">
                            {selectedRisk.due_date ? new Date(selectedRisk.due_date).toLocaleDateString() : 'No due date'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">Treatment:</span>
                          <p className="font-medium">{selectedRisk.treatment_option}</p>
                        </div>
                      </div>
                    </div>

                    {/* Asset Info */}
                    {selectedRisk.asset_details && (
                      <div className="bg-white border rounded-lg p-4">
                        <h5 className="text-sm font-medium text-gray-900 mb-3">Associated Asset</h5>
                        <div className="space-y-2 text-sm">
                          <p className="font-medium">{selectedRisk.asset_details.name}</p>
                          <p className="text-gray-600">{selectedRisk.asset_details.business_unit}</p>
                          <p className="text-gray-600">{selectedRisk.asset_details.type}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Note Modal */}
      <AnimatePresence>
        {showNoteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-lg w-full"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Add Note</h3>
                  <button
                    onClick={() => {
                      setShowNoteModal(false);
                      setNoteFormData({ content: '' });
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note Content</label>
                    <textarea
                      rows={4}
                      value={noteFormData.content}
                      onChange={(e) => setNoteFormData({ content: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your note..."
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowNoteModal(false);
                      setNoteFormData({ content: '' });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAddNote(selectedRisk.id)}
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Add Note
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RiskRegister;